import React, { useLayoutEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Input, CustomSelect, ConfirmModal, LoadingButton, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError } from '@redpanda/ui';
import { useNumberInput, useUnsavedChangesWarning } from '@redpanda/utils';
import { createExpense, updateExpense } from '../services/api';
import { PMExpense, PMPackage, EXPENSE_CATEGORIES } from '../types/pm';
import { toDateInput, toISOString, getTodayDateInput } from '../utils/dateUtils';
import { notifyDataChanged } from '../utils/dataEvents';

interface ExpenseDialogProps {
  projectId: string;
  expense?: PMExpense;
  packages?: Array<Pick<PMPackage, 'id' | 'name'>>;
  onClose: () => void;
}

interface FormData {
  expenseDate: string;
  amount: number | '';
  category: string;
  vendor: string;
  packageId: string;
  description: string;
}

/** 新增／編輯外部成本（管理員）：外包、採購、軟體授權、差旅… */
const ExpenseDialog: React.FC<ExpenseDialogProps> = ({ projectId, expense, packages = [], onClose }) => {
  const { t } = useTranslation(['translation', 'pages']);
  const { register, handleSubmit, formState: { errors, isDirty }, setValue, reset, watch } = useForm<FormData>();
  const [loading, setLoading] = useState(false);
  const amountInput = useNumberInput('integer');
  const isEditMode = !!expense;
  const tp = (key: string) => t(`pmExpenses.${key}`, { ns: 'pages' });

  const { handleClose, showConfirm, handleConfirmClose, handleCancelClose } = useUnsavedChangesWarning(isDirty, onClose);

  useLayoutEffect(() => {
    reset({
      expenseDate: expense ? toDateInput(expense.expenseDate) : getTodayDateInput(),
      amount: expense?.amount ?? '',
      category: expense?.category ?? 'OUTSOURCING',
      vendor: expense?.vendor ?? '',
      packageId: expense?.packageId ?? '',
      description: expense?.description ?? '',
    });
  }, [expense, reset]);

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      const payload = {
        expenseDate: toISOString(data.expenseDate)!,
        amount: Number(data.amount),
        category: data.category as PMExpense['category'],
        vendor: data.vendor.trim() || null,
        packageId: data.packageId || null,
        description: data.description.trim() || null,
      };
      if (isEditMode) await updateExpense(expense.id, payload);
      else await createExpense(projectId, payload);
      toast.success(t(isEditMode ? 'common.messages.updateSuccess' : 'common.messages.createSuccess'));
      notifyDataChanged('expenses');
      onClose();
    } catch (error: any) {
      toast.error(error.message || tp('saveError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogShell onClose={handleClose} closeDisabled={loading} className="max-w-xl">
        <DialogHeader title={tp(isEditMode ? 'editTitle' : 'addTitle')} onClose={handleClose} closeDisabled={loading} closeLabel={t('common.actions.close')} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <FormFieldWithError label={tp('fields.amount')} required error={errors.amount?.message} htmlFor="expenseAmount">
                <Input
                  type="text"
                  id="expenseAmount"
                  {...amountInput.bind(register('amount', { required: tp('amountRequired'), ...amountInput.validationRules }))}
                  variant={errors.amount ? 'error' : 'default'}
                  placeholder="0"
                />
              </FormFieldWithError>
              <FormFieldWithError label={tp('fields.expenseDate')} required error={errors.expenseDate?.message} htmlFor="expenseDate">
                <Input
                  type="date"
                  id="expenseDate"
                  {...register('expenseDate', { required: t('common.forms.validation.required') })}
                  variant={errors.expenseDate ? 'error' : 'default'}
                />
              </FormFieldWithError>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormFieldWithError label={tp('fields.category')} required htmlFor="expenseCategory">
                <CustomSelect
                  id="expenseCategory"
                  value={watch('category')}
                  onChange={(value) => setValue('category', value, { shouldDirty: true })}
                  options={EXPENSE_CATEGORIES.map(c => ({ value: c, label: t(`pmExpenses.category.${c}`, { ns: 'pages' }) }))}
                />
              </FormFieldWithError>
              <FormFieldWithError label={tp('fields.vendor')} error={errors.vendor?.message} htmlFor="expenseVendor">
                <Input
                  type="text"
                  id="expenseVendor"
                  {...register('vendor', { maxLength: { value: 100, message: t('common.forms.validation.maxLength', { length: 100 }) } })}
                  variant={errors.vendor ? 'error' : 'default'}
                />
              </FormFieldWithError>
            </div>

            {packages.length > 0 && (
              <FormFieldWithError label={tp('fields.package')} htmlFor="expensePackage">
                <CustomSelect
                  id="expensePackage"
                  value={watch('packageId')}
                  onChange={(value) => setValue('packageId', value, { shouldDirty: true })}
                  options={[
                    { value: '', label: t('pmPayments.noPackage', { ns: 'pages' }) },
                    ...packages.map(p => ({ value: p.id, label: p.name })),
                  ]}
                />
              </FormFieldWithError>
            )}

            <FormFieldWithError label={tp('fields.description')} error={errors.description?.message} htmlFor="expenseDescription">
              <Input
                type="text"
                id="expenseDescription"
                {...register('description', { maxLength: { value: 500, message: t('common.forms.validation.maxLength', { length: 500 }) } })}
                variant={errors.description ? 'error' : 'default'}
              />
            </FormFieldWithError>
          </div>

          <DialogFooter>
            <button type="button" onClick={handleClose} className={dialogButtonClass.cancel} disabled={loading}>
              {t('common.actions.cancel')}
            </button>
            <LoadingButton
              type="submit"
              loading={loading}
              isEditMode={isEditMode}
              customText={{ create: t('common.actions.create'), createLoading: t('common.status.creating') }}
              t={t}
            />
          </DialogFooter>
        </form>
      </DialogShell>

      <ConfirmModal isOpen={showConfirm} onClose={handleCancelClose} onConfirm={handleConfirmClose} title={t('components.modal.unsavedChanges.title')} message={t('components.modal.unsavedChanges.message')} confirmText={t('components.modal.unsavedChanges.confirmButton')} cancelText={t('components.modal.unsavedChanges.cancelButton')}
        t={t} />
    </>
  );
};

export default ExpenseDialog;
